import { useQuery } from '@tanstack/react-query';

import {
  search,
  type SearchData,
  type SearchDocument,
  searchQueryKey,
  type SearchResponse,
} from '@cfa/pathway-fe-shared/xp';

import { consolidateSearchDocuments } from '@/utils/consolidateSearchDocuments';

interface AddProcedureSearchSelected {
  summary?: SearchResponse;
  visibleResults: SearchDocument[];
}

/**
 * Add-content search: one GET /search, per-resource dedupe.
 *
 * Not `useExploreSearch`: explore wires smart-search flags, separate query cache,
 * and `search_result` analytics. Add Procedure uses plain search params and a
 * dedicated query-key suffix so popup searches do not share or evict Explore cache.
 *
 * @todo pathway-xp-api: Use deduped GET /search for add-content when XP ships (PATHWAY-4127).
 */
export const useAddProcedureSearchResults = (
  searchParams: SearchData['query'],
  searchTermQuery: string,
) => {
  const searchKey = [
    ...searchQueryKey({ query: searchParams }),
    'addProcedureConsolidated',
  ] as const;

  return useQuery({
    queryKey: searchKey,
    queryFn: async () => {
      const result = await search({
        query: {
          pageNumber: 1,
          ...searchParams,
        },
      });
      return result.data;
    },
    enabled: !!searchTermQuery,
    select: (data): AddProcedureSearchSelected => {
      const raw = data?.results ?? [];
      const visibleResults = consolidateSearchDocuments(raw);

      return {
        summary: data,
        visibleResults,
      };
    },
  });
};
